import React, { useContext, useEffect, useRef, useState } from "react";
import { Form, Formik, useFormikContext } from "formik";
import axios from "axios";
import { Avatar, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputAdornment, InputLabel, MenuItem, Select, Switch, Tab, Tabs, TextField, Typography } from "@material-ui/core";
import { Close, PersonOutline, PhotoCameraOutlined, Visibility, VisibilityOff } from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { getBackendUrl } from "../../config";
import ContactsTheme from "../../pages/Contacts/theme";
import { initialValues, normalizeUser, permissions, userPayload, userSchema } from "./form";
import useStyles from "./styles";

function ValidationFocus({ setTab }) {
  const { errors, submitCount, isValidating, isSubmitting } = useFormikContext();
  const shown = useRef(0);
  useEffect(() => {
    if (!submitCount || shown.current === submitCount || isValidating || isSubmitting || !Object.keys(errors).length) return;
    shown.current = submitCount;
    const field = Object.keys(errors)[0];
    setTab(["startWork", "endWork"].includes(field) ? "access" : "data");
    const timer = setTimeout(() => document.getElementById(`user-${field}`)?.focus(), 0);
    return () => clearTimeout(timer);
  }, [errors, submitCount, isValidating, isSubmitting, setTab]);
  return null;
}

function UserEditor({ onClose, userId, onSaved }) {
  const classes = useStyles();
  const { user: account } = useContext(AuthContext);
  const administrator = account.profile === "admin";
  const [values, setValues] = useState(initialValues);
  const [companyId, setCompanyId] = useState(account.companyId);
  const [queues, setQueues] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [revision, setRevision] = useState(0);
  const [tab, setTab] = useState("data");
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState("");
  const [photoError, setPhotoError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const saving = useRef(false);
  const persistedId = useRef(userId || null);
  const fileInput = useRef();

  useEffect(() => {
    let active = true;
    const cancel = axios.CancelToken.source();
    const config = { cancelToken: cancel.token, timeout: 20000 };
    setLoading(true); setLoadError(false);
    Promise.all([
      userId ? api.get(`/users/${userId}`, config) : Promise.resolve({ data: initialValues }),
      administrator ? api.get("/queue", config) : Promise.resolve({ data: [] }),
      administrator ? api.get("/whatsapp/?session=0", config) : Promise.resolve({ data: [] })
    ]).then(([person, queueList, connectionList]) => {
      if (!active) return;
      setValues(userId ? normalizeUser(person.data) : { ...initialValues, queueIds: [] });
      setCompanyId(person.data.companyId || account.companyId);
      setQueues(queueList.data); setConnections(connectionList.data);
    }).catch(err => { if (active && !axios.isCancel(err)) setLoadError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; cancel.cancel(); };
  }, [userId, administrator, account.companyId, revision]);

  useEffect(() => {
    if (!photo) { setPreview(""); return undefined; }
    const url = URL.createObjectURL(photo); setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  const close = () => { if (!saving.current) onClose(); };
  const pickPhoto = event => {
    const file = event.target.files[0];
    event.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type) || file.size > 5 * 1024 * 1024) {
      setPhotoError("Escolha uma imagem JPG, PNG ou WebP de até 5 MB."); return;
    }
    setPhotoError(""); setSaveError(""); setPhoto(file);
  };

  const save = async (formValues, actions) => {
    if (saving.current) return;
    saving.current = true; setSaveError("");
    let saved;
    try {
      const payload = userPayload(formValues, administrator);
      const response = persistedId.current ? await api.put(`/users/${persistedId.current}`, payload) : await api.post("/users", payload);
      saved = response.data;
      persistedId.current = saved.id;
      if (photo) {
        const data = new FormData(); data.append("profileImage", photo);
        const result = await api.post(`/users/${saved.id}/media-upload`, data);
        saved = result.data.user;
      }
      if (Number(saved.id) === Number(account.id)) {
        if (saved.profileImage) localStorage.setItem("profileImage", saved.profileImage);
        else localStorage.removeItem("profileImage");
      }
      if (onSaved) onSaved(saved);
      toast.success("Usuário salvo com sucesso.");
      onClose();
      if (Number(saved.id) === Number(account.id)) window.location.reload();
    } catch (err) {
      if (saved) {
        if (onSaved) onSaved(saved);
        setSaveError("O usuário foi salvo, mas a foto não foi enviada. Tente salvar novamente para concluir.");
      } else { setSaveError("Não foi possível salvar. Confira os dados e tente novamente."); toastError(err); }
    } finally { saving.current = false; actions.setSubmitting(false); }
  };

  return <Dialog open onClose={close} fullWidth maxWidth={false} scroll="paper" classes={{ paper: classes.paper }} aria-labelledby="user-modal-title">
    <DialogTitle disableTypography className={classes.title}>
      <span className={classes.titleIcon}><PersonOutline /></span><Typography component="h2" id="user-modal-title" className={classes.titleText}>{userId ? "Editar usuário" : "Novo usuário"}</Typography>
      <IconButton aria-label="Fechar formulário" onClick={close} className={classes.close}><Close /></IconButton>
    </DialogTitle>
    {loading || loadError ? <div className={classes.loading}>{loading ? <><CircularProgress size={28} /><Typography>Carregando usuário…</Typography></> : <><Typography role="alert">Não foi possível carregar o formulário.</Typography><Button color="primary" onClick={() => setRevision(value => value + 1)}>Tentar novamente</Button><Button onClick={close}>Cancelar</Button></>}</div> :
      <Formik initialValues={values} validationSchema={userSchema(!persistedId.current, administrator)} onSubmit={save}>
        {({ values: formValues, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting }) => {
          const field = (name, label, props = {}) => <TextField id={`user-${name}`} name={name} label={label} value={formValues[name]} onChange={handleChange} onBlur={handleBlur} error={Boolean(touched[name] && errors[name])} helperText={touched[name] && errors[name]} disabled={isSubmitting} variant="outlined" size="small" fullWidth {...props} />;
          const select = (name, label, options) => <TextField select id={`user-${name}`} name={name} label={label} value={formValues[name]} onChange={handleChange} disabled={isSubmitting} variant="outlined" size="small" fullWidth>{options.map(([value, text]) => <MenuItem key={value} value={value}>{text}</MenuItem>)}</TextField>;
          const photoUrl = preview || (formValues.profileImage ? `${getBackendUrl()}/public/company${companyId}/user/${formValues.profileImage}` : undefined);
          return <Form className={classes.form} noValidate>
            <ValidationFocus setTab={setTab} />
            <Tabs value={tab} onChange={(_, value) => setTab(value)} indicatorColor="primary" textColor="primary" className={classes.tabs} aria-label="Seções do usuário">
              <Tab label="Dados" value="data" id="user-tab-data" aria-controls="user-panel-data" disabled={isSubmitting} />
              {administrator && <Tab label="Acesso e filas" value="access" id="user-tab-access" aria-controls="user-panel-access" disabled={isSubmitting} />}
              <Tab label="Preferências" value="preferences" id="user-tab-preferences" aria-controls="user-panel-preferences" disabled={isSubmitting} />
            </Tabs>
            <DialogContent className={classes.content}>
              {saveError && <div role="alert" className={classes.error}>{saveError}</div>}
              <div role="tabpanel" id="user-panel-data" aria-labelledby="user-tab-data" hidden={tab !== "data"} className={classes.panel}>
                <div className={classes.photoRow}>
                  <Avatar className={classes.avatar} src={photoUrl} alt="Foto do usuário">{formValues.name.trim().slice(0, 1).toUpperCase() || <PersonOutline />}</Avatar>
                  <div><div className={classes.photoControls}>
                    <Button variant="outlined" startIcon={<PhotoCameraOutlined />} onClick={() => fileInput.current.click()} disabled={isSubmitting}>{photoUrl ? "Alterar foto" : "Escolher foto"}</Button>
                    {photoUrl && <Button disabled={isSubmitting} onClick={() => { setPhoto(null); setPhotoError(""); setFieldValue("profileImage", null); }}>Remover foto</Button>}
                  </div><Typography className={classes.caption}>JPG, PNG ou WebP · até 5 MB</Typography></div>
                  <input ref={fileInput} id="avatar-upload" aria-label="Foto do usuário" className={classes.photoInput} type="file" accept="image/jpeg,image/png,image/webp" onChange={pickPhoto} disabled={isSubmitting} />
                </div>
                {photoError && <div role="alert" className={classes.error}>{photoError}</div>}
                {field("name", "Nome", { autoComplete: "name" })}
                {field("email", "E-mail", { type: "email", autoComplete: "email" })}
                {field("password", userId ? "Nova senha" : "Senha", {
                  type: showPassword ? "text" : "password", autoComplete: "new-password",
                  helperText: touched.password && errors.password ? errors.password : userId ? "Deixe em branco para manter a senha atual." : "Pelo menos 5 caracteres.",
                  InputProps: { endAdornment: <InputAdornment position="end"><IconButton size="small" type="button" disabled={isSubmitting} aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"} onClick={() => setShowPassword(value => !value)}>{showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}</IconButton></InputAdornment> }
                })}
              </div>
              {administrator && <div role="tabpanel" id="user-panel-access" aria-labelledby="user-tab-access" hidden={tab !== "access"} className={classes.panel}>
                <div className={classes.grid}>
                  {select("profile", "Perfil de acesso", [["user", "Atendente"], ["admin", "Administrador"]])}
                  {select("whatsappId", "WhatsApp padrão", [["", "Nenhum"], ...connections.map(connection => [connection.id, connection.name])])}
                </div>
                <FormControl variant="outlined" size="small" fullWidth disabled={isSubmitting}>
                  <InputLabel id="user-queues-label">Filas de atendimento</InputLabel>
                  <Select name="queueIds" labelId="user-queues-label" id="user-queues" label="Filas de atendimento" multiple value={formValues.queueIds} onChange={handleChange}
                    renderValue={ids => <div className={classes.chips}>{ids.map(id => { const queue = queues.find(item => item.id === id); return <Chip key={id} size="small" className={classes.queueChip} label={<><span className={classes.queueDot} style={{ background: queue?.color }} />{queue?.name || `Fila #${id}`}</>} />; })}</div>}>
                    {!queues.length && <MenuItem disabled>Nenhuma fila cadastrada</MenuItem>}
                    {queues.map(queue => <MenuItem key={queue.id} value={queue.id} className={classes.queueOption}><span className={classes.queueDot} style={{ background: queue.color }} />{queue.name}</MenuItem>)}
                  </Select>
                </FormControl>
                <div><Typography className={classes.sectionTitle}>HORÁRIO DE TRABALHO</Typography><div className={classes.grid}>
                  {field("startWork", "Início", { type: "time", InputLabelProps: { shrink: true }, inputProps: { step: 60 } })}
                  {field("endWork", "Fim", { type: "time", InputLabelProps: { shrink: true }, inputProps: { step: 60 } })}
                </div></div>
                <div><Typography className={classes.sectionTitle}>PERMISSÕES</Typography><div className={classes.permissions}>{permissions.map(permission => <label key={permission.name} className={classes.permission}>
                  <span>{permission.label}</span><Switch size="small" color="primary" name={permission.name} checked={formValues[permission.name] === permission.enabled} onChange={(_, checked) => setFieldValue(permission.name, checked ? permission.enabled : permission.disabled)} disabled={isSubmitting} inputProps={{ "aria-label": permission.label }} />
                </label>)}</div></div>
              </div>}
              <div role="tabpanel" id="user-panel-preferences" aria-labelledby="user-tab-preferences" hidden={tab !== "preferences"} className={classes.panel}>
                <div className={classes.grid}>{select("defaultTheme", "Tema padrão", [["light", "Claro"], ["dark", "Escuro"]])}{select("defaultMenu", "Menu lateral", [["open", "Aberto"], ["closed", "Recolhido"]])}</div>
                {field("farewellMessage", "Mensagem de encerramento", { multiline: true, rows: 4 })}
              </div>
            </DialogContent>
            <DialogActions className={classes.footer}>
              <Button onClick={close} disabled={isSubmitting} variant="outlined">Cancelar</Button>
              <Button type="submit" color="primary" variant="contained" disabled={isSubmitting}>{isSubmitting && <CircularProgress size={16} className={classes.busy} />}{isSubmitting ? "Salvando…" : userId ? "Salvar alterações" : "Criar usuário"}</Button>
            </DialogActions>
          </Form>;
        }}
      </Formik>}
  </Dialog>;
}

export default function UserModal({ open, ...props }) {
  return open ? <ContactsTheme><UserEditor key={props.userId || "new"} {...props} /></ContactsTheme> : null;
}
